interface IconProps {
  className?: string;
  size?: number;
}

export function ArchitectIcon({ className = "", size = 24 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      {/* Main bulb outline */}
      <path
        d="M12 3C9.2 3 7 5.2 7 8C7 9.5 7.7 10.8 8.5 11.8C9 12.5 9.5 13.2 9.5 14V15.5C9.5 16.3 10.2 17 11 17H13C13.8 17 14.5 16.3 14.5 15.5V14C14.5 13.2 15 12.5 15.5 11.8C16.3 10.8 17 9.5 17 8C17 5.2 14.8 3 12 3Z"
        fill="currentColor"
        fillOpacity="0.1"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <animate
          attributeName="fill-opacity"
          values="0.1;0.3;0.1"
          dur="3s"
          repeatCount="indefinite"
        />
      </path>
      
      {/* Bulb base/screw threads */}
      <path
        d="M10 17H14M10.5 18H13.5M11 19H13"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.8"
      >
        <animate
          attributeName="opacity"
          values="0.6;1;0.6"
          dur="2s"
          repeatCount="indefinite"
        />
      </path>
      
      {/* Filament lines */}
      <path
        d="M10 7L12 9L14 7M10 9L12 11L14 9M11 11H13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        opacity="0.8"
      >
        <animate
          attributeName="opacity"
          values="0.3;1;0.3"
          dur="6s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="stroke-dasharray"
          values="0,20;20,0;0,20"
          dur="6s"
          repeatCount="indefinite"
        />
      </path>
      
      {/* Blueprint corner marks */}
      <path
        d="M5 3H3V5M19 3H21V5M5 21H3V19M19 21H21V19"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.6"
      >
        <animate
          attributeName="opacity"
          values="0.3;0.8;0.3"
          dur="6s"
          repeatCount="indefinite"
        />
      </path>
      
      {/* Construction guides */}
      <path
        d="M12 1V3M12 21V23M1 12H3M21 12H23"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeDasharray="3,3"
        opacity="0.5"
      >
        <animateTransform
          attributeName="transform"
          type="rotate"
          values="0 12 12;360 12 12"
          dur="25s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="stroke-dashoffset"
          values="0;-6"
          dur="1.5s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="opacity"
          values="0.3;0.7;0.3"
          dur="6s"
          repeatCount="indefinite"
        />
      </path>
    </svg>
  );
}

export function PerfectionistIcon({ className = "", size = 24 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      {/* Outer targeting circle - slowly rotating */}
      <circle
        cx="12"
        cy="12"
        r="10"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeDasharray="8,4"
        opacity="0.3"
      >
        <animateTransform
          attributeName="transform"
          type="rotate"
          values="0 12 12;360 12 12"
          dur="12s"
          repeatCount="indefinite"
        />
      </circle>
      
      {/* Four separate crosshair lines - always visible as base */}
      <path
        d="M12 2V8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.4"
      />
      
      <path
        d="M12 16V22"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.4"
      />
      
      <path
        d="M2 12H8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.4"
      />
      
      <path
        d="M16 12H22"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.4"
      />
      
      {/* Middle precision ring - appears in sequence */}
      <circle
        cx="12"
        cy="12"
        r="6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0"
      >
        <animate
          attributeName="opacity"
          values="0;1;1;1;0"
          dur="4s"
          repeatCount="indefinite"
        />
      </circle>
      
      {/* Crosshair connectors - connect the crosshairs */}
      <path
        d="M12 8V10M12 14V16M8 12H10M14 12H16"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0"
      >
        <animate
          attributeName="opacity"
          values="0;0;1;1;0"
          dur="4s"
          repeatCount="indefinite"
        />
      </path>
      
      {/* Inner core - appears last before reset */}
      <circle
        cx="12"
        cy="12"
        r="3"
        fill="currentColor"
        fillOpacity="0.2"
        stroke="currentColor"
        strokeWidth="2"
        opacity="0"
      >
        <animate
          attributeName="opacity"
          values="0;0;0;1;0"
          dur="4s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="fill-opacity"
          values="0.2;0.2;0.2;0.6;0.2"
          dur="4s"
          repeatCount="indefinite"
        />
      </circle>
      
      {/* Secondary crosshairs - removed as not needed */}
      
      {/* Center precision dot */}
      <circle
        cx="12"
        cy="12"
        r="1.5"
        fill="currentColor"
        opacity="0"
      >
        <animate
          attributeName="opacity"
          values="0;0;0;1;0"
          dur="4s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="r"
          values="1.5;1.5;1.5;2;0"
          dur="4s"
          repeatCount="indefinite"
        />
      </circle>
    </svg>
  );
}

export function WatcherIcon({ className = "", size = 24 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      {/* Scanning ring */}
      <circle
        cx="12"
        cy="12"
        r="10"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeDasharray="12,6"
        opacity="0.4"
      >
        <animateTransform
          attributeName="transform"
          type="rotate"
          values="0 12 12;360 12 12"
          dur="4s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="opacity"
          values="0.2;0.6;0.2"
          dur="2s"
          repeatCount="indefinite"
        />
      </circle>
      
      {/* Eye outer shape */}
      <ellipse
        cx="12"
        cy="12"
        rx="8"
        ry="5"
        fill="currentColor"
        fillOpacity="0.1"
        stroke="currentColor"
        strokeWidth="2"
      >
        <animate
          attributeName="fill-opacity"
          values="0.1;0.2;0.1"
          dur="3s"
          repeatCount="indefinite"
        />
      </ellipse>
      
      {/* Iris */}
      <circle
        cx="12"
        cy="12"
        r="3"
        fill="currentColor"
        fillOpacity="0.3"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <animate
          attributeName="r"
          values="3;3.5;3"
          dur="2.5s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="fill-opacity"
          values="0.3;0.5;0.3"
          dur="2s"
          repeatCount="indefinite"
        />
        <animateTransform
          attributeName="transform"
          type="translate"
          values="0,0;1.5,0;1.5,0;0,0;0,0;-1.5,0;-1.5,0;0,0"
          dur="6s"
          repeatCount="indefinite"
        />
      </circle>
      
      {/* Pupil */}
      <circle
        cx="12"
        cy="12"
        r="1.5"
        fill="currentColor"
      >
        <animate
          attributeName="r"
          values="1.5;1;1.5"
          dur="4s"
          repeatCount="indefinite"
        />
        <animateTransform
          attributeName="transform"
          type="translate"
          values="0,0;1.5,0;1.5,0;0,0;0,0;-1.5,0;-1.5,0;0,0"
          dur="6s"
          repeatCount="indefinite"
        />
      </circle>
      
      {/* Surveillance scan lines */}
      <path
        d="M5 12H8M16 12H19M12 5V8M12 16V19"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        opacity="0.7"
      >
        <animate
          attributeName="opacity"
          values="0.3;0.9;0.3"
          dur="1.5s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="stroke-dasharray"
          values="0,8;8,0;0,8"
          dur="2s"
          repeatCount="indefinite"
        />
      </path>
    </svg>
  );
}